// draft-producer.ts (Story 7.8) — the offline write-path PRODUCER: the half that WRITES the
// device-local `drafts` row and APPENDS the `outbound_queue` ops that Story 7.4's `drain-core`
// (the consumer) later replays to the server. 7.4 drains; nothing produced those ops until here.
//
// Mirrors the drain-core / drain-runner split for the same two reasons:
//   1. PURITY/TESTABILITY — the core functions (`startEvaluationDraft`, `saveEvaluationDraft`)
//      take everything they touch (the Dexie DB, the online transport, the clock, the id minter,
//      the drain trigger) via an injected `ProducerDeps`, so they unit-test with `fake-indexeddb`
//      + a `vi.fn()` transport and NO React, NO real network. `buildProducerDeps()` supplies the
//      real wiring (the npm `db` singleton, `apiFetch`, `triggerMainThreadDrain`).
//   2. ONE source of truth for the local write contract — the `DraftRecord` / `OutboundOp` shapes
//      and the client-minted-id rule come straight from the 7.3 schema, so a producer write and a
//      drain replay can never disagree about the row/op layout.
//
// This module is MAIN-THREAD ONLY — since seam-74 the Service Worker runs no drain and touches no
// IndexedDB — so importing `api-client` / `idb-schema` in the wiring is fine; there is no SW bundle
// to keep Dexie out of.
//
// Architectural decision (story Dev Notes — "Online-direct + offline-enqueue"):
//   * CREATE is ALWAYS queue-mediated. There is no direct-online create path in the app —
//     `POST /evaluations/create` is reached ONLY by the drain replaying a `create` op (drain-runner).
//     So `startEvaluationDraft` mints the id, writes the `drafts` row, enqueues the `create` op, and
//     triggers a drain: online, the triggered drain replays it immediately (201); offline, it waits.
//     One code path, online or offline; the client-minted UUID is the IndexedDB key, the
//     `outbound_queue.eval_id`, and (after sync) the server primary key — one id end-to-end.
//   * UPDATE keeps today's synchronous online UX: online → direct `saveOnline` then re-anchor the
//     local `drafts.version` from the response (preserves the 6.5 save-on-blur "saved" ack); offline
//     (or a network failure mid-save) → write the local row + enqueue/collapse an `update` op. The
//     local `drafts` row is ALWAYS the source of truth the UI reads.

import { isOwnedBy } from "./idb-schema-def";
import type { DraftRecord, OutboundOp } from "./idb-schema-def";
import type { EvaluationDraftData } from "@/hooks/queries/evaluation-types";

// --- Injected dependencies (structural — the real `db` and a test double both satisfy these) ---

/** The two Dexie tables the producer touches, typed structurally so the npm `Table<…>` and a
 *  hand-rolled test stub both satisfy them. `where(index).equals(key).toArray()` backs the
 *  collapse-on-edit lookup (the 7.3 `outbound_queue.eval_id` index). */
export interface ProducerDb {
  drafts: {
    get(key: string): Promise<DraftRecord | undefined>;
    put(draft: DraftRecord): Promise<string>;
  };
  outbound_queue: {
    where(index: string): { equals(key: string): { toArray(): Promise<OutboundOp[]> } };
    put(op: OutboundOp): Promise<string>;
    update(key: string, changes: Partial<OutboundOp>): Promise<number>;
  };
}

export interface ProducerDeps {
  /** The shared `franklin` IndexedDB handle (main-thread singleton; a fake in tests). */
  db: ProducerDb;
  /** Online/offline routing signal. Minimal seam (`isEffectivelyOnline`); Story 7.7 hardens it. */
  isOnline: () => boolean | Promise<boolean>;
  /**
   * Direct online save transport for the UPDATE path — `POST /evaluations/{id}/save-draft` with
   * `X-Client-Version`. Returns the re-anchored `version` (+ `state`) from the server. Throws on
   * any non-2xx (`ApiError`): a network failure (status 0) routes to the offline enqueue fallback;
   * any other error (409 conflict / 422 / …) propagates to the caller (conflict UX is 6.7 / 7.5).
   * CREATE has no online transport here — it is queue-mediated (drained via `triggerDrain`).
   */
  saveOnline: (args: {
    evalId: string;
    version: number;
    data: EvaluationDraftData;
  }) => Promise<{ version: number; state: string }>;
  /** True when a thrown `saveOnline` error is a transport/offline failure (→ enqueue), not a real
   *  server rejection (→ rethrow). Real wiring: `err instanceof ApiError && err.status === 0`. */
  isOfflineError: (err: unknown) => boolean;
  /** Fire-and-forget drain trigger (7.4 `triggerMainThreadDrain`); failures leave the op queued. */
  triggerDrain: () => void;
  /**
   * Optional Dexie `rw` transaction wrapper over `drafts` + `outbound_queue`, so the local draft
   * write and the queue enqueue COMMIT TOGETHER (or roll back together). Without it, a crash /
   * tab-close between the two writes can orphan a draft with no `create` op (it never syncs) or
   * drop an edit from the queue, and the collapse read-modify-write can race two concurrent saves
   * into two ops. Mirrors drain-core's `runAtomic`. When absent the steps run directly (same
   * end-state, no crash-safety) — the real wiring (`buildProducerDeps`) always supplies it; only a
   * bare unit stub omits it. The wrapped `fn` must touch ONLY `drafts`/`outbound_queue` and await
   * no non-Dexie promise (a network call would auto-commit the transaction).
   */
  runAtomic?: (fn: () => Promise<void>) => Promise<void>;
  /** ISO-8601 clock, injected so timestamps are deterministic under test. */
  now: () => string;
  /** Client-minted UUID source (`crypto.randomUUID()`), injected so ids are deterministic in tests. */
  newId: () => string;
  /**
   * Story 19.6 Unit E — resolve the current device owner (`AuthUser.userId`), stamped onto every row
   * this producer INSERTS so a shared device can scope its local reads to the clinician who made
   * them. Real wiring: `readOwnerIdentity` (the durable `meta.owner_identity` key + its in-memory
   * mirror). Resolved OUTSIDE `runAtomic` — it reads the `meta` table, which is not part of the
   * producer's `rw` transaction.
   *
   * REQUIRED, like `DrainDeps.owner` (A-01, 19.6 review). Was optional; an absent resolver wrote
   * rows UNATTRIBUTED, which are hidden from local reads and excluded from the drain — a silent
   * data loss. A caller that genuinely has no identity should pass an explicit resolver returning
   * `undefined`; when the resolver returns `undefined`, the producer does NOT insert rather than
   * writing an unattributed row. Real wiring (`buildProducerDeps`) always supplies `readOwnerIdentity`.
   *
   * This resolver is ALSO the adoption source: a save into an already-existing UNATTRIBUTED row
   * (a pre-v3 row the v3 upgrade deliberately did not guess at) stamps this owner onto the row
   * and onto the evaluation's unattributed queued ops. See `upsertDraftData` and
   * `adoptUnattributedOps`. A row owned by someone ELSE is never re-attributed — and never
   * written at all: the save is refused (A-02) rather than overwriting its body.
   */
  owner: () => Promise<string | undefined>;
}

/** The outcome of an UPDATE save.
 *
 * Three distinct outcomes share this type:
 *  - `{ synced: true,  refused: false, version }` — committed to the server; re-anchor to `version`.
 *  - `{ synced: false, refused: false, version }` — enqueued offline; version unchanged.
 *  - `{ synced: false, refused: true,  version }` — save refused: the local row belongs to another
 *    clinician (see `writeIfOwned`). Nothing was written; `version` is unchanged. Callers must NOT
 *    treat this as a successful offline save — in particular, error banners must not be cleared.
 *    This path is unreachable today (all reads are owner-scoped) but the contract must be honest. */
export type SaveDraftOutcome =
  | { synced: boolean; refused: false; version: number }
  | { synced: false; refused: true; version: number };

// --- Submit guard (AC7) ----------------------------------------------------------------------

/** Thrown by `startEvaluationDraft` (and future write paths) when no device owner could be
 *  resolved and writing would produce an unattributed row. The real wiring always resolves an
 *  owner before any UI action; this error indicates a programming error in the caller's wiring. */
export class NoIdentityError extends Error {
  constructor(message = "Producer write requires a resolved owner identity") {
    super(message);
    this.name = "NoIdentityError";
  }
}

/** Thrown by `guardSubmitOnline` when a submit is attempted offline. Submit is online-only (the
 *  7.7 gate) — it is NEVER enqueued, so an offline submit must hard-fail rather than silently queue. */
export class OfflineSubmitError extends Error {
  constructor(message = "Submit is online-only and cannot be queued offline") {
    super(message);
    this.name = "OfflineSubmitError";
  }
}

/**
 * AC7 — submit stays online-only and is NEVER produced as an op. The producer's only op kinds are
 * `create`/`update` (the `OutboundOp.op` union has no `submit`), so submit can never reach the
 * queue by construction; this guard makes the offline-submit attempt fail loudly for the caller
 * (Story 6.6's submit flow) instead of being dropped. A no-op when online.
 */
export function guardSubmitOnline(online: boolean): void {
  if (!online) throw new OfflineSubmitError();
}

// --- CREATE (AC1, AC2) -----------------------------------------------------------------------

export interface StartDraftInput {
  /** The existing, caller-owned client (patient) the evaluation is for. */
  clientId: string;
  /** Optional initial draft body (defaults to `{}` — a brand-new eval has no content yet). */
  data?: EvaluationDraftData;
  /** Optional pre-minted id (tests / a caller that already minted one). Defaults to `newId()`. */
  id?: string;
}

/**
 * Start a brand-new evaluation, offline-first (AC1, AC2). Always queue-mediated:
 *  1. mint the evaluation id locally (`newId()` → UUIDv4) — it exists on-device before any network,
 *  2. write the `drafts` row (`version: 0`, `last_synced_version: null`, `state: "draft"`),
 *  3. append a `create` `OutboundOp` whose payload is the BE `POST /evaluations/create` contract
 *     (`{ id, client_id }`) — the draft *content* syncs later via `update` ops (the create endpoint
 *     only seeds the skeleton), and
 *  4. trigger a drain (AC6) so an online start replays immediately while an offline one waits.
 *
 * Returns the minted id (== `drafts.id` == `outbound_queue.eval_id` == future server PK).
 */
export async function startEvaluationDraft(
  deps: ProducerDeps,
  input: StartDraftInput,
): Promise<{ id: string }> {
  const id = input.id ?? deps.newId();
  const ts = deps.now();
  // Resolved BEFORE `withAtomic` — it reads the `meta` table, which is not in the producer's
  // `rw` transaction, and a non-Dexie await inside one would auto-commit it.
  const owner = await resolveOwner(deps);
  // A-01 (19.6 review) — do NOT insert an unattributed row. A start without a resolved owner
  // would write a draft the local reads hide and the drain skips — silent data loss. The real
  // wiring always resolves an owner before any UI action, so this is a programming-error guard.
  if (!owner) throw new NoIdentityError("startEvaluationDraft requires a resolved owner");

  const draft: DraftRecord = {
    id,
    // Story 19.6 Unit E — device-level data isolation on a shared device (cache hygiene, NOT an
    // access-control boundary: RLS + the endpoint remain the guarantee). Stamped at CREATE so the
    // offline list/detail/readiness/ledger reads can scope to this clinician.
    owner,
    // gh-459 — persist the client id at CREATE so an offline reopen can rebuild an
    // `EvaluationDetail` from this row alone (`upsertDraftData` spreads `...existing`, so it
    // survives every later save; the online-GET write-through covers resume-of-server evals).
    client_id: input.clientId,
    version: 0,
    last_synced_version: null,
    state: "draft",
    conflict: null,
    data: (input.data ?? {}) as Record<string, unknown>,
    created_at: ts,
    updated_at: ts,
  };

  const op: OutboundOp = {
    id: deps.newId(),
    eval_id: id,
    // Story 19.6 Unit E — the queue carries the owner too, so the drain replays only the current
    // clinician's ops. Without it the next session would submit this clinician's PHI under its own
    // token, which is worse than merely displaying it.
    owner,
    op: "create",
    // The BE create contract is `{ client_id, id }` only (extra fields are ignored). The minted id
    // rides in the payload so the replay POSTs it as the server primary key (idempotent create).
    payload: { id, client_id: input.clientId },
    client_version: 0,
    attempts: 0,
    last_error: null,
    queued_at: ts,
  };

  // Atomic: the draft row and the create op commit together (or roll back together), so a
  // crash / tab-close between the two writes can never leave a draft with no `create` op to sync.
  await withAtomic(deps, async () => {
    await deps.db.drafts.put(draft);
    await deps.db.outbound_queue.put(op);
  });
  deps.triggerDrain();

  return { id };
}

// --- UPDATE (AC3, AC4, AC5, AC6) -------------------------------------------------------------

export interface SaveDraftInput {
  /** The evaluation (draft) to save into — the local `drafts.id` / server PK. */
  evalId: string;
  /** The current server-acknowledged version → the `update` op's `client_version` / `X-Client-Version`. */
  version: number;
  /** The ENTIRE current draft body (full-replace contract — never a delta; matches 6.2's save-draft). */
  data: EvaluationDraftData;
}

/**
 * Save an edit to an existing draft (AC3–AC6), routed on `isOnline()`:
 *  - online → write the local `drafts` row (source of truth the UI reads), then `saveOnline`
 *    (direct `POST …/save-draft`) and re-anchor `drafts.version` / `last_synced_version` / `state`
 *    from the response. A real server rejection (409/422/…) propagates; a transport/offline failure
 *    falls through to the enqueue path so the edit is never lost.
 *  - offline → write the local row AND append (or COLLAPSE — AC4) an `update` op carrying the full
 *    payload + the current `client_version`, ATOMICALLY (one `rw` transaction), so a crash can't
 *    split them and a burst of offline edits leaves exactly ONE pending op per `eval_id`.
 * Either branch triggers a drain afterward (AC6).
 */
export async function saveEvaluationDraft(
  deps: ProducerDeps,
  input: SaveDraftInput,
): Promise<SaveDraftOutcome> {
  const { evalId, version, data } = input;
  const ts = deps.now();
  // Resolved once, outside every transaction (see `startEvaluationDraft`). Three uses: stamped on a
  // row/op this save INSERTS, adopted onto an UNATTRIBUTED existing one, and compared against an
  // existing owner to refuse a save into another clinician's evaluation entirely.
  const owner = await resolveOwner(deps);
  // A-05 (19.6 review) — mirror the guard in `startEvaluationDraft`: a save without a resolved
  // owner would write an unattributed row the list hides and the drain skips — silent data loss.
  if (!owner) throw new NoIdentityError("saveEvaluationDraft requires a resolved owner");

  if (await deps.isOnline()) {
    // Local write first (the source of truth the UI reads) — UPSERT so an eval created ONLINE
    // earlier (no local `drafts` row, only hydrated from the server on resume) still gets a
    // device-local row — then save directly and re-anchor from the response.
    //
    // Wrapped in the same `rw` transaction the offline branch uses, so the row's adoption (below)
    // and the adoption of its queued ops commit together on BOTH branches: an adopted draft whose
    // ops stayed unattributed would be visible but permanently undrainable. Only the two local
    // tables are touched here — `saveOnline` (the network) stays strictly outside.
    const written = await writeIfOwned(deps, evalId, owner, async () => {
      await upsertDraftData(deps, evalId, version, data, ts, owner);
      await adoptUnattributedOps(deps, evalId, owner);
    });
    // Refused (the evaluation's local rows are another clinician's — see `writeIfOwned`): return
    // WITHOUT touching the network. `mutateDraft` below re-anchors `version`/`state`/`conflict` on
    // whatever row is at this id with no owner check of its own, so falling through would half-write
    // the very row whose body we just declined to overwrite.
    // A-02 (19.6 review) — return a distinct `refused: true` so the caller can distinguish a
    // refused save from a real offline queue. `{ synced: false }` alone is ambiguous; the scoring
    // step must NOT clear its error banner on a refusal.
    if (!written) return { synced: false, refused: true, version };
    try {
      const res = await deps.saveOnline({ evalId, version, data });
      await mutateDraft(deps, evalId, (d) => {
        d.version = res.version;
        d.last_synced_version = res.version;
        // Honor the server-authoritative state; guard the closed `"draft" | "submitted"` union so
        // an unexpected string is ignored rather than corrupting `state` (normally "draft").
        if (res.state === "draft" || res.state === "submitted") d.state = res.state;
        d.conflict = null;
      });
      deps.triggerDrain();
      return { synced: true, refused: false, version: res.version };
    } catch (err) {
      // Only a transport/offline failure falls back to the queue; a real server rejection
      // (version conflict, validation, …) is the caller's / resolver's to handle.
      if (!deps.isOfflineError(err)) throw err;
    }
  }

  // Offline (or an online save that hit a transport failure and fell back): write the local row
  // AND enqueue/collapse the `update` op ATOMICALLY, so a crash between them can't leave the edit
  // saved locally but missing from the queue, and two concurrent saves can't both append an op.
  const written = await writeIfOwned(deps, evalId, owner, async () => {
    await upsertDraftData(deps, evalId, version, data, ts, owner);
    await adoptUnattributedOps(deps, evalId, owner);
    await enqueueUpdateCollapsing(deps, evalId, version, data, ts, owner);
  });
  // Nothing was written, so there is nothing new for the drain to carry (and the one op that exists
  // for this eval is another clinician's, which the drain skips anyway).
  if (written) deps.triggerDrain();
  // A-02 (19.6 review) — refused when `!written`; a real offline queue returns `refused: false`.
  return { synced: false, refused: written ? false : true, version };
}

// --- Internal helpers ------------------------------------------------------------------------

/** Run `fn` inside the injected `rw` transaction when the wiring provides one; otherwise run it
 *  directly (same writes, no atomicity). Mirrors drain-core's `runAtomic` fallback. */
function withAtomic(deps: ProducerDeps, fn: () => Promise<void>): Promise<void> {
  return deps.runAtomic ? deps.runAtomic(fn) : fn();
}

/** Story 19.6 Unit E — the current device owner, or `undefined` when the resolver cannot
 *  identify the caller. `deps.owner` is required (A-01, 19.6 review) — callers with no identity
 *  pass an explicit resolver returning `undefined` rather than omitting the field. */
function resolveOwner(deps: ProducerDeps): Promise<string | undefined> {
  return deps.owner();
}

/**
 * Story 19.6 Unit E (19.6 review round 3) — is EVERY local row this evaluation already has writable
 * by `owner`? One gate for the whole save, evaluated before anything is written.
 *
 * Round 2 gave `upsertDraftData` and `enqueueUpdateCollapsing` a refusal each, and a per-function
 * refusal cannot keep the promise the refusal is for: a save runs both, so each state where exactly
 * one of them fired left the evaluation HALF-WRITTEN —
 *   • a foreign `drafts` row with no pending `update` op: the row write was refused, then the enqueue
 *     found nothing to collapse into and APPENDED a fresh op owned by the current clinician carrying
 *     their body against the other clinician's eval id. The drain then owns an op the server refuses
 *     forever, surfacing in the wrong clinician's ledger for an evaluation that is not theirs;
 *   • a foreign pending op with no `drafts` row: the row was SEEDED for the current clinician and the
 *     enqueue refused, so the edit was saved locally and never queued — precisely the split
 *     `runAtomic` exists to prevent, while the caller still got `{synced: false}` ("saved offline").
 *
 * So the verdict is taken once, over both tables, for the whole evaluation: refuse if the `drafts`
 * row OR any queued op for this eval id is attributed to somebody else. Wider than the two refusals
 * it replaces, deliberately — a foreign pending `create` is the same hazard as a foreign `update` (an
 * appended op under this owner against an eval the server will never accept from them).
 *
 * The three intended cases are unchanged: FOREIGN → refuse; UNATTRIBUTED (a pre-v3 row the v3
 * upgrade deliberately did not guess at) → fall through and be adopted; OWN → save. An unresolvable
 * `owner` matches nothing (`isOwnedBy` fails closed), so it can adopt/insert but can never write into
 * an attributed row.
 */
async function isEvaluationWritableBy(
  deps: ProducerDeps,
  evalId: string,
  owner: string | undefined,
): Promise<boolean> {
  const existing = await deps.db.drafts.get(evalId);
  if (existing && existing.owner !== undefined && !isOwnedBy(existing, owner)) return false;
  const pending = await deps.db.outbound_queue.where("eval_id").equals(evalId).toArray();
  return !pending.some((op) => op.owner !== undefined && !isOwnedBy(op, owner));
}

/**
 * Run a save's local-write `body` inside the atomic wrapper, gated ONCE on
 * `isEvaluationWritableBy` — so every write the body performs happens, or none of them does.
 * Resolves `true` when the body ran, `false` when the save was refused; the caller must not proceed
 * past a `false` (see `saveEvaluationDraft`). The gate read is inside the same transaction as the
 * writes it guards, so a concurrent save cannot land a foreign row between check and write.
 */
async function writeIfOwned(
  deps: ProducerDeps,
  evalId: string,
  owner: string | undefined,
  body: () => Promise<void>,
): Promise<boolean> {
  let written = false;
  await withAtomic(deps, async () => {
    if (!(await isEvaluationWritableBy(deps, evalId, owner))) return;
    written = true;
    await body();
  });
  return written;
}

/** Upsert the local `drafts` row's `data`/`updated_at`. Preserves `version`/`last_synced_version`/
 *  `state`/`conflict`/`created_at` on an existing row (version only advances on a server ack); seeds
 *  a fresh row at the caller's known `version` when absent (online-created, resume-only eval),
 *  stamped with the CURRENT owner since the row is an insert.
 *
 *  OWNERSHIP (Story 19.6 Unit E, 19.6 review rounds 2-3) — three cases, not two:
 *   • a row owned by a DIFFERENT clinician is not written AT ALL. Preserving `existing.owner` while
 *     overwriting `data` kept the owner LABEL honest and handed that clinician's draft body away
 *     anyway — the opposite policy from `useGetEvaluation`'s `writeThroughDraftRow`, which returns on
 *     the same state because "overwriting one would be data loss on top of the breach". Same policy
 *     here now — but enforced ONE level up, in `isEvaluationWritableBy`/`writeIfOwned`, for the whole
 *     save rather than per helper: a refusal that only this function honoured still let the paired
 *     `enqueueUpdateCollapsing` append an op, which is the half-write round 3 closes. This function
 *     is therefore only ever reached for a row it MAY write. (Unreachable in the app either way —
 *     every read is owner-scoped, so this clinician cannot see that row to edit it — but the
 *     read-modify-write path must not be the hole, and the file must not assert a guarantee it does
 *     not deliver.)
 *   • an UNATTRIBUTED row (a pre-v3 row the v3 upgrade deliberately did not guess at — see
 *     `upgradeToV3`) is ADOPTED by the clinician saving into it. Writing into a draft is the second
 *     of the two moments ownership is proven; without this the row would stay ownerless, hidden from
 *     its own author's reads and excluded from the drain forever. With no resolvable `owner` it stays
 *     unattributed rather than acquiring an invented one.
 *   • the clinician's OWN row is saved normally. */
async function upsertDraftData(
  deps: ProducerDeps,
  evalId: string,
  version: number,
  data: EvaluationDraftData,
  ts: string,
  owner: string | undefined,
): Promise<void> {
  const existing = await deps.db.drafts.get(evalId);
  const next: DraftRecord = existing
    ? {
        ...existing,
        owner: existing.owner ?? owner,
        data: data as Record<string, unknown>,
        updated_at: ts,
      }
    : {
        id: evalId,
        owner,
        version,
        last_synced_version: null,
        state: "draft",
        conflict: null,
        data: data as Record<string, unknown>,
        created_at: ts,
        updated_at: ts,
      };
  await deps.db.drafts.put(next);
}

/**
 * Story 19.6 Unit E (19.6 review) — the queue half of adoption, the twin of `upsertDraftData`'s
 * `existing.owner ?? owner`. When this clinician's save adopts an unattributed `drafts` row, the
 * evaluation's UNATTRIBUTED queued ops must be adopted in the same transaction: the drain skips
 * unattributed ops, so an adopted draft sitting on ownerless ops is visible but permanently
 * undrainable — and that un-drained work is the clinical data the whole offline path exists to
 * protect. Runs on both save branches, inside the producer's `rw` transaction.
 *
 * Touches ONLY `owner === undefined`. An op belonging to a DIFFERENT clinician is never adopted (that
 * would replay their PHI under this token, the exact breach Unit E closes), and a falsy `owner`
 * adopts nothing at all — fail closed.
 *
 * The `cache_metadata` `ledger:*` row is NOT adopted here: it is not in the producer's transaction
 * scope (`drafts` + `outbound_queue` only), and it is DERIVED state — the next `reconcileLedger` pass
 * upserts `ledger:<eval_id>` stamped with the current owner, which overwrites the stale unattributed
 * row at the same key. The server-proof path (`adoptUnattributedEvaluationRows`) does adopt it, since
 * it owns its own transaction and can widen the scope safely.
 */
async function adoptUnattributedOps(
  deps: ProducerDeps,
  evalId: string,
  owner: string | undefined,
): Promise<void> {
  if (!owner) return;
  const pending = await deps.db.outbound_queue.where("eval_id").equals(evalId).toArray();
  for (const op of pending) {
    if (op.owner === undefined) await deps.db.outbound_queue.update(op.id, { owner });
  }
}

/**
 * Collapse-on-edit (AC4): keep at most ONE pending `update` op per `eval_id`. If one already exists
 * (found via the `eval_id` index), replace its payload/version/timestamp in place and reset its
 * retry budget (it carries fresh content); otherwise append a new op. A pending `create` for the
 * same eval is left untouched — drain-core sequences the chain `[create, latest-update]`, so the
 * create must survive (collapsing across op kinds would orphan the update against a never-created eval).
 */
async function enqueueUpdateCollapsing(
  deps: ProducerDeps,
  evalId: string,
  version: number,
  data: EvaluationDraftData,
  ts: string,
  owner: string | undefined,
): Promise<void> {
  const pending = await deps.db.outbound_queue.where("eval_id").equals(evalId).toArray();
  const existingUpdate = pending.find((o) => o.op === "update");
  const payload = data as Record<string, unknown>;

  if (existingUpdate) {
    // Story 19.6 Unit E (19.6 review round 3) — an op owned by a DIFFERENT clinician must not be
    // collapsed into (its `payload` is the clinical body the drain replays under THAT owner's
    // identity) AND must not be sidestepped by appending a fresh op under this owner against that
    // clinician's eval id. Neither is possible here any more: `isEvaluationWritableBy` refuses the
    // whole save — draft row and op together — before this function is reached, so this collapse only
    // ever runs on an op this clinician may write. Round 2 enforced it as a bare `return` right here
    // and claimed the eval was "left entirely alone"; it was not — with no pending `update` to find,
    // the append below ran regardless. Unreachable in the app either way: an evaluation id is minted
    // once, for one clinician.
    //
    // A collapse UPDATES an existing op, so `owner` is deliberately absent from the change set —
    // the op keeps whoever queued it (a collapse is not a change of ownership). An op that was
    // UNATTRIBUTED has already been adopted by `adoptUnattributedOps` earlier in this same
    // transaction, so "keeps whoever queued it" is not a way of staying ownerless.
    await deps.db.outbound_queue.update(existingUpdate.id, {
      payload,
      client_version: version,
      queued_at: ts,
      attempts: 0,
      last_error: null,
    });
    return;
  }

  await deps.db.outbound_queue.put({
    id: deps.newId(),
    eval_id: evalId,
    owner, // Story 19.6 Unit E — an INSERT, so it carries the current owner.
    op: "update",
    payload,
    client_version: version,
    attempts: 0,
    last_error: null,
    queued_at: ts,
  });
}

/** Read-modify-write a draft row by id. No-op if absent (it was just upserted, so this finds it). */
async function mutateDraft(
  deps: ProducerDeps,
  evalId: string,
  fn: (draft: DraftRecord) => void,
): Promise<void> {
  const draft = await deps.db.drafts.get(evalId);
  if (!draft) return;
  fn(draft);
  await deps.db.drafts.put(draft);
}
